import React, { useState, useEffect } from 'react';
import { getBookings, createBooking, deleteBooking, updateBookingStatus, getCustomers } from '../lib/api';
import api from '../lib/api';
import { formatCurrency, CURRENCY_SYMBOL } from '../lib/currency';
import { BOOKINGS } from '../constants/testIds';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ExportImportButtons } from '../components/ExportImportButtons';
import { DateFilter } from '../components/DateFilter';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, MoreVertical, Trash2, MapPin, Calendar, Users, Loader2, ClipboardList, Search, Zap, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
];

const packageTypes = [
  { value: 'custom', label: 'Custom Package' },
  { value: 'honeymoon', label: 'Honeymoon' },
  { value: 'family', label: 'Family Vacation' },
  { value: 'adventure', label: 'Adventure' },
  { value: 'business', label: 'Business Travel' },
  { value: 'pilgrimage', label: 'Pilgrimage' },
];

const getStatusClass = (status) => {
  const classes = {
    pending: 'bg-amber-50 text-amber-700',
    confirmed: 'bg-emerald-50 text-emerald-700',
    cancelled: 'bg-red-50 text-red-700',
    completed: 'bg-slate-100 text-slate-700',
  };
  return classes[status] || 'bg-slate-100 text-slate-700';
};

const getPaymentStatusClass = (status) => {
  const classes = {
    pending: 'bg-amber-50 text-amber-700',
    partial: 'bg-blue-50 text-blue-700',
    paid: 'bg-emerald-50 text-emerald-700',
  };
  return classes[status] || 'bg-slate-100 text-slate-700';
};

export const Bookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [tripCodeSearch, setTripCodeSearch] = useState('');
  const [fetchingTrip, setFetchingTrip] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    trip_code: '',
    destination: '',
    travel_date: '',
    return_date: '',
    travelers: 1,
    package_type: 'custom',
    total_amount: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, [filterStatus]);

  const loadData = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (filterMonth) params.month = filterMonth;
      const [bookingsRes, customersRes, tripsRes] = await Promise.all([
        getBookings(params),
        getCustomers(),
        api.get('/trips'),
      ]);
      setBookings(bookingsRes.data);
      setCustomers(customersRes.data);
      setTrips(tripsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch trip details by code
  const fetchTripByCode = async (code) => {
    if (!code || code.length < 3) return;
    setFetchingTrip(true);
    try {
      const { data } = await api.get(`/trips/code/${code.toUpperCase()}`);
      setSelectedTrip(data);
      // Auto-fill form with trip details
      const endDate = new Date(data.trip_date);
      endDate.setDate(endDate.getDate() + (data.days - 1));
      
      setFormData({
        ...formData,
        trip_code: data.trip_code,
        destination: data.destination,
        travel_date: data.trip_date,
        return_date: endDate.toISOString().split('T')[0],
        total_amount: data.cost_per_person || '',
      });
      toast.success(`Trip found: ${data.trip_name}`);
    } catch (error) {
      setSelectedTrip(null);
      if (error.response?.status === 404) {
        toast.error('Trip code not found');
      }
    } finally {
      setFetchingTrip(false);
    }
  };

  // Handle trip code input change with debounce
  const handleTripCodeChange = (value) => {
    setFormData({ ...formData, trip_code: value.toUpperCase() });
    setTripCodeSearch(value.toUpperCase());
  };

  // Handle trip selection from dropdown
  const handleTripSelect = (tripId) => {
    const trip = trips.find(t => t.id === tripId);
    if (trip) {
      setSelectedTrip(trip);
      const endDate = new Date(trip.trip_date);
      endDate.setDate(endDate.getDate() + (trip.days - 1));
      
      setFormData({
        ...formData,
        trip_code: trip.trip_code || '',
        destination: trip.destination,
        travel_date: trip.trip_date,
        return_date: endDate.toISOString().split('T')[0],
        total_amount: trip.cost_per_person || '',
      });
    }
  };

  const handleDateFilter = () => loadData();
  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setFilterMonth('');
    loadData();
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      trip_code: '',
      destination: '',
      travel_date: '',
      return_date: '',
      travelers: 1,
      package_type: 'custom',
      total_amount: '',
      notes: '',
    });
    setSelectedTrip(null);
    setTripCodeSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await createBooking({
        ...formData,
        total_amount: parseFloat(formData.total_amount) || 0,
        travelers: parseInt(formData.travelers) || 1,
      });
      toast.success('Booking created successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    try {
      await deleteBooking(id);
      toast.success('Booking deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateBookingStatus(id, newStatus);
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid={BOOKINGS.container} className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>Booking Management</h1>
          <p className="text-muted-foreground mt-1">Manage travel bookings and reservations</p>
        </div>
        <div className="flex items-center gap-3">
          <DateFilter
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            month={filterMonth}
            setMonth={setFilterMonth}
            onApply={handleDateFilter}
            onClear={clearDateFilter}
          />
          <ExportImportButtons module="bookings" onImportSuccess={loadData} />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid={BOOKINGS.addBtn} onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              New Booking
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-outfit">Create Booking</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Trip Code Auto-Fetch Section */}
              <div className="p-4 bg-gradient-to-r from-[#84bd0e]/10 to-[#383838]/10 rounded-lg space-y-3">
                <Label className="font-semibold flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#383838]" />
                  Quick Fill from Trip Code
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      value={formData.trip_code}
                      onChange={(e) => handleTripCodeChange(e.target.value)}
                      placeholder="Enter Trip Code (e.g., KF-2026-0001)"
                      className="pr-10 font-mono"
                    />
                    {fetchingTrip && (
                      <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    )}
                  </div>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => fetchTripByCode(formData.trip_code)}
                    disabled={fetchingTrip || !formData.trip_code}
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
                {trips.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Or select from existing trips:</Label>
                    <Select value="" onValueChange={handleTripSelect}>
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Select a trip to auto-fill" />
                      </SelectTrigger>
                      <SelectContent>
                        {trips.filter(t => t.status === 'active').map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            <span className="font-mono text-xs mr-2">{t.trip_code || 'N/A'}</span>
                            {t.trip_name} - {t.destination}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {selectedTrip && (
                  <div className="text-xs bg-white p-2 rounded border border-[#383838]/30">
                    <span className="font-semibold text-[#383838]">Selected:</span> {selectedTrip.trip_name} • {selectedTrip.destination} • {selectedTrip.days} Days • ₹{parseInt(selectedTrip.cost_per_person || 0).toLocaleString('en-IN')}/person
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Destination *</Label>
                  <Input
                    value={formData.destination}
                    onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                    placeholder="e.g., Dubai"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Package Type</Label>
                  <Select value={formData.package_type} onValueChange={(v) => setFormData({ ...formData, package_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {packageTypes.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Travel Date *</Label>
                  <Input
                    type="date"
                    value={formData.travel_date}
                    onChange={(e) => setFormData({ ...formData, travel_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Return Date *</Label>
                  <Input
                    type="date"
                    value={formData.return_date}
                    onChange={(e) => setFormData({ ...formData, return_date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Travelers</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.travelers}
                    onChange={(e) => setFormData({ ...formData, travelers: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total Amount ({CURRENCY_SYMBOL}) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Booking
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="text-slate-500">Filter by status:</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bookings</SelectItem>
                {statusOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table data-testid={BOOKINGS.table}>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Trip Code</TableHead>
                <TableHead className="font-semibold">Customer</TableHead>
                <TableHead className="font-semibold">Destination</TableHead>
                <TableHead className="font-semibold">Dates</TableHead>
                <TableHead className="font-semibold">Package</TableHead>
                <TableHead className="font-semibold text-right">Amount</TableHead>
                <TableHead className="font-semibold">Payment</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.length > 0 ? (
                bookings.map((b) => (
                  <TableRow key={b.id} className="hover:bg-slate-50">
                    <TableCell>
                      {b.trip_code ? (
                        <Badge variant="outline" className="font-mono text-xs bg-[#84bd0e]/10 text-[#84bd0e] border-[#84bd0e]/30">
                          {b.trip_code}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{getCustomerName(b.customer_id)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <MapPin className="w-3 h-3" />
                        {b.destination}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Calendar className="w-3 h-3" />
                        {b.travel_date}
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{b.package_type}</TableCell>
                    <TableCell className="text-right">
                      <div>
                        <p className="font-semibold">{formatCurrency(b.total_amount)}</p>
                        {b.paid_amount > 0 && (
                          <p className="text-xs text-slate-500">Paid: {formatCurrency(b.paid_amount)}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPaymentStatusClass(b.payment_status)}>
                        {b.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select value={b.status} onValueChange={(v) => handleStatusChange(b.id, v)}>
                        <SelectTrigger className={`w-28 h-8 text-xs ${getStatusClass(b.status)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/booking-details?booking_id=${b.id}`)}>
                            <ClipboardList className="w-4 h-4 mr-2" />
                            Manage Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${b.id}/invoice`, '_blank')}>
                            <FileText className="w-4 h-4 mr-2" />
                            Generate Invoice
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(b.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No bookings found. Click "New Booking" to create one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
